import VelavuAssetDevicePair from "src/data/asset-device-pair";
import React, { useCallback, useMemo, useState } from "react";
import { AssetDetailTab } from "./dropdown/asset-detail/asset-detail";
import { NotFoundError, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { useVelavuToast } from "../../elements/velavu-toast";
import { ToastNotificationNoContent } from "../../elements/toast-notification-builtins";

export interface SelectedAssetState {
	assetDevicePair: VelavuAssetDevicePair | undefined;
	selectAssetDevicePair: (
		pair: VelavuAssetDevicePair | undefined,
		tab?: AssetDetailTab,
	) => void;
	assetDetailTab: AssetDetailTab;
	setAssetDetailTab: (tab: AssetDetailTab) => void;
	refresh: () => Promise<void>;
}

/**
 * State for the selected asset and device pair
 */
export function useSelectedAssetState(): SelectedAssetState {
	const { pushToast } = useVelavuToast();

	const [selectedAssetDevicePair, setSelectedAssetDevicePair] = useState<
		VelavuAssetDevicePair | undefined
	>(undefined);
	const [assetDetailTab, setAssetDetailTab] = useState(AssetDetailTab.Info);

	const selectAssetDevicePair = useCallback(
		(
			pair: VelavuAssetDevicePair | undefined,
			tab: AssetDetailTab = AssetDetailTab.Info,
		) => {
			setSelectedAssetDevicePair((previousPair) => {
				//Set the tab if we're selecting this asset from an unselected state
				if (previousPair?.asset.id !== pair?.asset.id) {
					setAssetDetailTab(tab);
				}

				//Set the new pair
				return pair;
			});
		},
		[setSelectedAssetDevicePair, setAssetDetailTab],
	);

	const refresh = useCallback(async () => {
		//Nothing to do if we don't have a selected asset / device pair
		if (
			selectedAssetDevicePair === undefined ||
			selectedAssetDevicePair.device === undefined
		) {
			return;
		}

		//Fetch the device from the backend
		let targetDevice: VelavuDevice;
		try {
			targetDevice = await VelavuAPI.devices.getSpecificDevice(
				selectedAssetDevicePair.device.id,
			);
		} catch (err) {
			if (err instanceof NotFoundError) {
				setSelectedAssetDevicePair(undefined);
				pushToast((close) => (
					<ToastNotificationNoContent onDismiss={close} />
				));
				return;
			} else {
				//Rethrow error
				throw err;
			}
		}

		//Make sure the device is still paired to the asset
		const targetAsset = targetDevice.asset;
		if (
			targetAsset === undefined ||
			targetAsset.id !== selectedAssetDevicePair.asset.id
		) {
			setSelectedAssetDevicePair(undefined);
			pushToast((close) => (
				<ToastNotificationNoContent onDismiss={close} />
			));
			return;
		}

		//Set the new asset device pair
		setSelectedAssetDevicePair({
			asset: targetAsset,
			device: targetDevice,
		});
	}, [selectedAssetDevicePair, setSelectedAssetDevicePair, pushToast]);

	return useMemo(
		() => ({
			assetDevicePair: selectedAssetDevicePair,
			selectAssetDevicePair,
			assetDetailTab,
			setAssetDetailTab,
			refresh,
		}),
		[
			selectedAssetDevicePair,
			selectAssetDevicePair,
			assetDetailTab,
			setAssetDetailTab,
			refresh,
		],
	);
}
